import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { TEST_OVERVIEW_SIDEPANEL_EXECUTION } from '@elementary/common-ui/constants/queryKeys';
import { gql } from 'graphql-request';
import { SourceType } from 'shared/types';
import {
  ModelFailureExecution,
  TestOverviewExecution,
} from 'shared/types/execution';

export const getTestOverviewExecutionQuery = gql`
  query GetTestOverviewExecution($id: String!) {
    testExecution(id: $id) {
      id
      query
      status
      exception
      executionTimestamp
      lastRowCount
      alertDestination {
        channel
        type
      }
      data {
        type
        ... on AutoFreshnessTestExecutionData {
          metrics
          trainingPeriod {
            count
            period
          }
        }
        ... on AnomalyChartTestExecutionData {
          metricsTitle
          failOnZero
          anomalyDirection
          detectionDelay {
            count
            period
          }
          detectionPeriod {
            count
            period
          }
          trainingPeriod {
            count
            period
          }
          metrics
        }
        ... on TableTestExecutionData {
          data
          headers
        }
        ... on SchemaChangesTestExecutionData {
          description
          displayName
          type
        }
        ... on CloudDataContractTestExecutionData {
          type
          changes {
            changeType
            columnName
            dataType
            previousDataType
            isNullable
            previousIsNullable
            dependencyImpact {
              downstreamTableAssets
              downstreamBiAssets
              totalDownstreamColumns
              hasCriticalDownstream
            }
          }
        }
        ... on FreshnessChartExecutionData {
          lastTimestamp
          errorAfterRange
          loadedAtField
          maxLoadedAtTimeAgoInS
          warnAfterRange
        }
      }
    }
  }
`;

export const getModelFailureExecutionQuery = gql`
  query GetModelFailureExecution($id: String!) {
    tableAssetExecution(id: $id) {
      id
      status
      exception
      query
      executionTimestamp
      alertDestination {
        channel
        type
      }
    }
  }
`;

function useTestOverviewExecution<T>(
  id: string | undefined,
  sourceType: SourceType,
) {
  const { data, isFetching, error } = useGraphqlQuery<
    | {
        testExecution: TestOverviewExecution;
      }
    | {
        tableAssetExecution: ModelFailureExecution;
      }
  >({
    queryKey: [TEST_OVERVIEW_SIDEPANEL_EXECUTION, id, sourceType],
    query:
      sourceType === SourceType.TEST
        ? getTestOverviewExecutionQuery
        : getModelFailureExecutionQuery,
    variables: {
      id,
    },
  });

  return {
    isFetching,
    data:
      data === undefined
        ? undefined
        : sourceType === SourceType.TEST
        ? ((data as { testExecution: TestOverviewExecution })
            .testExecution as T)
        : ((data as { tableAssetExecution: ModelFailureExecution })
            .tableAssetExecution as T),
    error,
  };
}

export default useTestOverviewExecution;
